import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Mail, MapPin, Send, Heart, ShieldCheck, HandHeart, Sparkles, Building2, Users } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTENT_MAP = {
  partner: "foundation-partner",
  volunteer: "foundation-volunteer",
  sponsor: "foundation-sponsor",
  donation: "foundation-donation",
  property: "foundation-property",
  expertise: "foundation-expertise",
  shelter: "foundation-shelter",
  general: "foundation-general",
};

export default function FoundationContact() {
  useSEO({
    title: "Partner With The Tandem T Foundation",
    description: "Partner with, volunteer for, donate to, or sponsor The Tandem T Foundation Inc. — a Florida non-profit supporting mothers and children rebuilding after domestic violence.",
    path: "/foundation/contact",
  });
  const [searchParams] = useSearchParams();
  const intent = searchParams.get("intent");
  const preselected = (intent && INTENT_MAP[intent]) || "foundation-partner";

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    company: "",
    phone: "",
    inquiry_type: preselected,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.full_name.length < 2) return toast.error("Please enter your full name.");
    if (!form.email.includes("@")) return toast.error("Please enter a valid email.");
    if (form.message.length < 10) return toast.error("Please add a few more details to your message.");

    try {
      setSubmitting(true);
      // Ensure inquiry_type stays clearly marked as foundation for backend routing
      const payload = {
        ...form,
        inquiry_type: form.inquiry_type.startsWith("foundation-") ? form.inquiry_type : `foundation-${form.inquiry_type}`,
      };
      await axios.post(`${API}/contact`, payload);
      toast.success("Thank you — your message reached The Tandem T Foundation. We'll be in touch soon.");
      setSubmitted(true);
      setForm({
        full_name: "",
        email: "",
        company: "",
        phone: "",
        inquiry_type: preselected,
        message: "",
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "rounded-none bg-transparent border-0 border-b border-white/25 focus-visible:ring-0 focus-visible:border-white px-0 py-6 text-white placeholder:text-white/30 text-base";

  return (
    <div data-testid="page-foundation-contact" className="pt-32 md:pt-40 pb-24 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
        {/* LEFT — Foundation identity + info */}
        <div className="md:col-span-5">
          <Reveal>
            <p className="overline text-white/55">The Tandem T Foundation</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="font-serif font-light text-white text-5xl md:text-7xl leading-[1] tracking-tight mt-6">
              Partner with <em className="italic">the Foundation.</em>
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="text-white/70 font-light leading-relaxed mt-8 max-w-md">
              Whether you&rsquo;re ready to partner, volunteer, donate, sponsor, or explore how your organization
              can support our community mission &mdash; this form reaches the Foundation team directly.
            </p>
          </Reveal>

          <div className="mt-12 space-y-6">
            <div className="flex items-start gap-4">
              <Building2 size={16} className="mt-1 text-white shrink-0" />
              <div data-testid="foundation-contact-org">
                <p className="overline text-white/45">Organization</p>
                <p className="text-white/90 mt-1 font-medium">The Tandem T Foundation Inc</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Users size={16} className="mt-1 text-white shrink-0" />
              <div data-testid="foundation-contact-leadership">
                <p className="overline text-white/45">Leadership</p>
                <p className="text-white/90 mt-1">Chrissy Thompson <span className="text-white/55">— Founder &amp; President</span></p>
                <p className="text-white/90 mt-1">Rohan Taylor <span className="text-white/55">— Co-Founder &amp; Vice President</span></p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Mail size={16} className="mt-1 text-white shrink-0" />
              <div>
                <p className="overline text-white/45">Foundation Email</p>
                <a
                  href="mailto:partners@tandemtfoundation.org"
                  className="text-white/90 mt-1 block hover:text-white"
                  data-testid="foundation-contact-email"
                >
                  partners@tandemtfoundation.org
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <MapPin size={16} className="mt-1 text-white shrink-0" />
              <div>
                <p className="overline text-white/45">Office</p>
                <p className="text-white/90 mt-1" data-testid="foundation-contact-address">
                  550 N Reo St, Suite 300, Tampa, FL 33609
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <ShieldCheck size={16} className="mt-1 text-white shrink-0" />
              <div>
                <p className="overline text-white/45">Legal Status</p>
                <p className="text-white/90 mt-1">Federally Recognized 501(c)(3) Nonprofit · EIN 42-3781424</p>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT — form */}
        <div className="md:col-span-7">
          <Reveal delay={0.1}>
            <form
              data-testid="foundation-contact-form"
              onSubmit={submit}
              className="border border-white/15 p-8 md:p-12 bg-white/[0.02]"
            >
              <div className="flex items-center gap-3 mb-8">
                <Heart size={16} className="text-white" />
                <p className="overline text-white/55">Foundation Inquiry Form</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                <div className="md:col-span-2">
                  <Label className="overline text-white/50">Full Name</Label>
                  <Input
                    data-testid="foundation-contact-input-name"
                    className={inputClass}
                    value={form.full_name}
                    onChange={(e) => update("full_name", e.target.value)}
                    placeholder="Your full name"
                    required
                  />
                </div>
                <div>
                  <Label className="overline text-white/50">Email</Label>
                  <Input
                    data-testid="foundation-contact-input-email"
                    type="email"
                    className={inputClass}
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@email.com"
                    required
                  />
                </div>
                <div>
                  <Label className="overline text-white/50">Phone</Label>
                  <Input
                    data-testid="foundation-contact-input-phone"
                    className={inputClass}
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="+1 (555) 555-5555"
                  />
                </div>
                <div>
                  <Label className="overline text-white/50">Organization (optional)</Label>
                  <Input
                    data-testid="foundation-contact-input-company"
                    className={inputClass}
                    value={form.company}
                    onChange={(e) => update("company", e.target.value)}
                    placeholder="Church, shelter, company, school…"
                  />
                </div>
                <div>
                  <Label className="overline text-white/50">How You&rsquo;d Like to Help</Label>
                  <Select
                    value={form.inquiry_type}
                    onValueChange={(v) => update("inquiry_type", v)}
                  >
                    <SelectTrigger
                      data-testid="foundation-contact-select-type"
                      className="rounded-none bg-transparent border-0 border-b border-white/25 focus:ring-0 px-0 py-6 text-white"
                    >
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-black border border-white/20 rounded-none text-white">
                      <SelectItem value="foundation-partner">Become a Community Partner</SelectItem>
                      <SelectItem value="foundation-volunteer">Volunteer Sign-Up</SelectItem>
                      <SelectItem value="foundation-sponsor">Corporate Sponsorship</SelectItem>
                      <SelectItem value="foundation-donation">Financial or In-Kind Donation</SelectItem>
                      <SelectItem value="foundation-property">Property or Facility Donation</SelectItem>
                      <SelectItem value="foundation-expertise">Professional / Pro-Bono Expertise</SelectItem>
                      <SelectItem value="foundation-shelter">Shelter / Nonprofit Collaboration</SelectItem>
                      <SelectItem value="foundation-general">General Inquiry</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2 mt-4">
                  <Label className="overline text-white/50">Your Message</Label>
                  <Textarea
                    data-testid="foundation-contact-input-message"
                    rows={5}
                    className="rounded-none bg-transparent border border-white/25 focus-visible:ring-0 focus-visible:border-white text-white placeholder:text-white/30 mt-3"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="Share how you&rsquo;d like to partner, volunteer, or support the Foundation…"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="foundation-contact-submit"
                disabled={submitting}
                className="group inline-flex items-center gap-3 mt-10 px-8 py-4 bg-white text-black overline hover:bg-white/85 disabled:opacity-50 transition-colors"
              >
                {submitting ? "Submitting…" : "Send to Foundation"}
                <Send size={14} className="hover-arrow" />
              </button>

              {submitted && (
                <p data-testid="foundation-contact-success" className="mt-6 text-white/75 text-sm font-light">
                  Thank you &mdash; your inquiry has reached The Tandem T Foundation. A team member will be in
                  touch shortly.
                </p>
              )}
            </form>

            {/* Quick-tap partner/volunteer cards */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="foundation-contact-quicklinks">
              <div className="p-6 border border-white/15 flex items-start gap-4">
                <Sparkles size={18} className="mt-1 text-white shrink-0" />
                <div>
                  <p className="overline text-white/55">Partners</p>
                  <p className="text-white/85 font-light text-sm mt-2">Corporate sponsors, shelters, faith organizations, agencies.</p>
                </div>
              </div>
              <div className="p-6 border border-white/15 flex items-start gap-4">
                <HandHeart size={18} className="mt-1 text-white shrink-0" />
                <div>
                  <p className="overline text-white/55">Volunteers</p>
                  <p className="text-white/85 font-light text-sm mt-2">Neighbors, professionals, and community members ready to serve.</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
